<script lang="ts" setup>
import type { CcxtMarket } from '@/services/types'
import { computed, watch } from 'vue'
import { i18n } from '@/i18n'
import { calcDecimals } from '@/utils'
import TriggerButton from '@/components/TriggerButton.vue'
import { Button } from 'primevue'
import Skeleton from 'primevue/skeleton'
import { useTrailingSlSettings } from '@/composable/settings/useTrailingSlSettings'

interface DexLegSpecificData {
  swapping: boolean
  usdtBalance: number | null
  tokenBalance: number | null
  balancesLoading: boolean
}

const props = defineProps<{
  amount: number
  cacheKeyPostfix: string
  chain: string
  contract: string
  asset: string
  slippage: number | null
  dexPriceBuy: number | null
  dexPriceSell: number | null
  priceCex: number | null
  spread: number | null | undefined
  backSpread: number | null | undefined
  market: CcxtMarket
  formData: DexLegSpecificData
}>()

const emit = defineEmits(['swap-dex-buy', 'swap-dex-sell', 'close-dex-position'])

const { t } = i18n.global
const priceRounding = computed(() => calcDecimals(props.market.precision.price))
const cacheKeyPostfix = computed(() => props.cacheKeyPostfix)
const { trailingSl, trailingExit, trailingSide, trailingNext, trailingActive, cleanTrailingSl } = useTrailingSlSettings(cacheKeyPostfix)

const dexPrice = computed(() => {
  if (props.dexPriceBuy && props.dexPriceSell) {
    return (props.dexPriceBuy + props.dexPriceSell) / 2
  }
  return props.dexPriceBuy ?? props.dexPriceSell ?? null
})

watch(
  [
    () => props.dexPriceBuy,
    () => props.dexPriceSell,
    () => props.priceCex,
  ],
  () => {
    // moveTslIfRequired()
    // checkIsTslBounded()
    // checkIsSpreadClosed()
  },
)

const moveTslIfRequired = () => {
  const cexPrice = props.priceCex
  const dexPrice_ = dexPrice.value

  if (!trailingSl.value || !trailingExit.value || !trailingNext.value || !cexPrice || !dexPrice_) {
    return
  }

  const isDexReachSl = (
    (trailingSide.value == "long" && dexPrice_ >= trailingExit.value)
    ||
    (trailingSide.value == "short" && dexPrice_ <= trailingExit.value)
  )
  const isCexReachTp = (
    (trailingSide.value == "long" && cexPrice >= trailingNext.value)
    ||
    (trailingSide.value == "short" && cexPrice <= trailingNext.value)
  )

  if (trailingActive.value && isCexReachTp || !trailingActive.value && isDexReachSl) {
    console.log(`trailingActive.value=${trailingActive.value}`)
    console.log(`isCexReachTp=${isCexReachTp}`)
    console.log(`isDexReachSl=${isDexReachSl}`)
    const rightPrice = cexPrice
    trailingActive.value = true
    const stepRatio = 1
    const priceRatio = rightPrice * stepRatio / 100
    if (trailingSide.value == "long") {
      trailingExit.value = rightPrice - priceRatio
      trailingNext.value = rightPrice + priceRatio
    } else {
      trailingExit.value = rightPrice + priceRatio
      trailingNext.value = rightPrice - priceRatio
    }
    console.log(`trailingNext=${trailingNext.value}`)
    console.log(`trailingExit=${trailingExit.value}`)
  }
}

const checkIsTslBounded = () => {
  const cexPrice = props.priceCex
  if (!trailingSl.value || !trailingExit.value || !cexPrice) {
    return
  }
  const isCexReachSl = (
    (trailingSide.value == "long" && cexPrice <= trailingExit.value)
    ||
    (trailingSide.value == "short" && cexPrice >= trailingExit.value)
  )
  if (isCexReachSl) {
    cleanTrailingSl()
    emit('close-dex-position')
  }
}

const MIN_SPREAD_CLOSE = 0.5

const checkIsSpreadClosed = () => {
  if (!trailingSl.value || !trailingExit.value) {
    return
  }
  const isSpreadClosed = (
    (trailingSide.value == "long" && props.spread != null && props.spread < MIN_SPREAD_CLOSE)
    ||
    (trailingSide.value == "short" && props.backSpread != null && props.backSpread < MIN_SPREAD_CLOSE)
  )
  if (isSpreadClosed) {
    cleanTrailingSl()
    emit('close-dex-position')
  }
}
</script>

<template>
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
    <TriggerButton class="col-span-2" :root="cacheKeyPostfix" :amount="amount" cache-key="dex">
    </TriggerButton>
    <Button fluid class="col-span-2" severity="success" variant="outlined" @click="trailingSl = !trailingSl"
      :icon="trailingSl ? 'pi pi-check-square' : 'pi pi-stop'" :label="t('modal.createSwapOrders.trailingSl')">
    </Button>
    <div class="col-span-1 text-center">
      <Button class="mb-2" @click="emit('swap-dex-buy')" fluid severity="success" :loading="formData.swapping"
        :disabled="formData.swapping || !Boolean(props.dexPriceBuy)"
        :label="t('modal.createSwapOrders.dexBuy', { price: props.dexPriceBuy ? Number(props.dexPriceBuy).toFixed(priceRounding) : '...' })">
      </Button>
    </div>
    <div class="col-span-1 text-center">
      <Button class="mb-2" @click="emit('swap-dex-sell')" fluid severity="danger" :loading="formData.swapping"
        :disabled="formData.swapping || !Boolean(props.dexPriceSell)"
        :label="t('modal.createSwapOrders.dexSell', { price: props.dexPriceSell ? Number(props.dexPriceSell).toFixed(priceRounding) : '...' })">
      </Button>
    </div>
  </div>
  <div class="grid grid-cols-1 lg:grid-cols-2">
    <div class="mt-2 text-center">
      <span v-if="formData.usdtBalance !== null" class="pit success">{{ t('balance') }}: {{
        Number(formData.usdtBalance).toFixed(2) }}
        USDT</span>
      <span v-else-if="formData.balancesLoading">
        <Skeleton width="100px" height="24px" />
      </span>
      <span v-else class="pit danger">{{ t('balance') }}: —</span>
    </div>

    <div class="mt-2 text-center">
      <span v-if="formData.tokenBalance !== null" class="pit danger">{{ t('balance') }}: {{
        Number(formData.tokenBalance).toFixed(priceRounding) }} {{ props.asset.toUpperCase() }}</span>
      <span v-else-if="formData.balancesLoading">
        <Skeleton width="100px" height="24px" />
      </span>
      <span v-else class="pit danger">{{ t('balance') }}: —</span>
    </div>
  </div>
</template>
